import { useCallback, useState, useMemo, useEffect } from 'react';
import { DialogCloseButton } from 'foundations-components/transitional/components/DialogCloseButton';
import { H2 } from 'foundations-components/transitional/components/H2';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { Paragraph } from 'foundations-components/transitional/components/Paragraph';
import { Tab } from 'foundations-components/transitional/components/Tabs';
import { Tabs } from 'foundations-components/transitional/components/Tabs';
import { FloatingAlertList } from 'foundations-components/transitional/components/FloatingAlertList';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { ModalPanel } from 'foundations-components/transitional/components/ModalPanel';
import { PanelHeader } from 'foundations-components/transitional/components/PanelHeader';
import { PanelBody } from 'foundations-components/transitional/components/PanelBody';
import { PanelSection } from 'foundations-components/transitional/components/Panel';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { InstallPermissions } from '../containers/InstallPermissions';
import { SuspenseLoadingState } from '../SuspenseLoadingState/SuspenseLoadingState';
import { DataPermissions } from '../containers/DataPermissions';
import { UnsavedChangesProvider, useUnsavedChanges, ACTION_TYPES } from '../../contexts/UnsavedChangesContext';
import { AppProvider, useApp } from '../../contexts/AppContext';
import { InstalledBy } from '../containers/InstalledBy';
import { RevokeApproval } from '../containers/RevokeApproval';
import { useFetchModalPermissions } from '../../hooks/queries/useFetchModalPermissions';
import { UninstallCallbackProvider } from '../../contexts/UninstallCallbackContext';
import { RevokeApprovalCallbackProvider } from '../../contexts/RevokeApprovalCallbackContext';
import { InstallPermissionsSaveCallbackProvider } from '../../contexts/InstallPermissionsSaveCallbackContext';
import { DataPermissionsProvider } from '../../contexts/DataPermissionsContext';
import { DataPermissionsFooter } from '../dataPermissions/DataPermissionsFooter';
import { InstallPermissionsProvider } from '../../contexts/InstallPermissionsContext';
import { InstallPermissionsDowngradeProvider } from '../../contexts/InstallPermissionsDowngradeContext';
import { InstallPermissionsFooter } from '../installPermissions/InstallPermissionsFooter';
import { DIALOG_WIDTH } from '../../constants/panel';
import { TrackingProvider, useTracking } from '../../contexts/TrackingContext';
import { TeamsGateProvider, useTeamsGate } from '../../contexts/TeamsGateContext';
import { filterGatedEntityTypes } from '../../utils/installPermissions';
import { SCREENS, EVENTS, SUBSCREENS } from '../../constants/tracking';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const MANAGEMENT_MODAL_PANEL_TABS = {
  INSTALLED_BY: 'INSTALLED_BY',
  INSTALL_PERMISSIONS: 'INSTALL_PERMISSIONS',
  DATA_PERMISSIONS: 'DATA_PERMISSIONS',
  REVOKE_APPROVAL: 'REVOKE_APPROVAL'
};
const TAB_TO_SUBSCREEN = {
  [MANAGEMENT_MODAL_PANEL_TABS.INSTALLED_BY]: SUBSCREENS.INSTALLED_BY,
  [MANAGEMENT_MODAL_PANEL_TABS.INSTALL_PERMISSIONS]: SUBSCREENS.INSTALL_PERMISSIONS,
  [MANAGEMENT_MODAL_PANEL_TABS.DATA_PERMISSIONS]: SUBSCREENS.DATA_PERMISSIONS,
  [MANAGEMENT_MODAL_PANEL_TABS.REVOKE_APPROVAL]: SUBSCREENS.REVOKE_APPROVAL
};
export function ManagementModalPanelContent({
  selectedTab,
  setSelectedTab,
  installCount
}) {
  const {
    requestAction
  } = useUnsavedChanges();
  const {
    data: {
      optionalAppPermissions,
      requiredAppPermissions,
      conditionallyRequiredAppPermissions,
      hasDataPermissions
    }
  } = useFetchModalPermissions();

  // If the data permissions tab is selected but there are no permissions, switch to the installed by tab
  // This could happen if the default tab is the data permissions tab, but likely won't happen in practice.
  useEffect(() => {
    if (!hasDataPermissions && selectedTab === MANAGEMENT_MODAL_PANEL_TABS.DATA_PERMISSIONS) {
      setSelectedTab(MANAGEMENT_MODAL_PANEL_TABS.INSTALLED_BY);
    }
  }, [hasDataPermissions, selectedTab, setSelectedTab]);
  const permissionsNotOnAppConfig = useMemo(() => [...optionalAppPermissions, ...requiredAppPermissions, ...conditionallyRequiredAppPermissions].filter(permission => !permission.isOnAppConfig), [optionalAppPermissions, requiredAppPermissions, conditionallyRequiredAppPermissions]);
  const handleTabChange = newTab => {
    requestAction(() => setSelectedTab(newTab));
  };
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsxs(Tabs, {
      onSelectedChange: e => handleTabChange(e.target.value),
      selected: selectedTab,
      fullHeightPanel: true,
      fill: true,
      panelStyles: {
        paddingInline: '0',
        paddingBlockStart: '600'
      },
      children: [/*#__PURE__*/_jsx(Tab, {
        tabId: MANAGEMENT_MODAL_PANEL_TABS.INSTALLED_BY,
        title:
        // Appears only if install count is a number
        installCount != null ? /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.modal.tabs.installedByWithCount",
          options: {
            count: installCount
          }
        }) : /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.modal.tabs.installedBy"
        }),
        children: /*#__PURE__*/_jsx(PanelSection, {
          children: /*#__PURE__*/_jsx(InstalledBy, {})
        })
      }), /*#__PURE__*/_jsx(Tab, {
        tabId: MANAGEMENT_MODAL_PANEL_TABS.INSTALL_PERMISSIONS,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.modal.tabs.installPermissions"
        }),
        "data-selenium": "install-permissions-tab",
        children: /*#__PURE__*/_jsx(PanelSection, {
          children: /*#__PURE__*/_jsx(InstallPermissions, {})
        })
      }), hasDataPermissions && /*#__PURE__*/_jsx(Tab, {
        tabId: MANAGEMENT_MODAL_PANEL_TABS.DATA_PERMISSIONS,
        "data-selenium": "data-permissions-tab",
        title: /*#__PURE__*/_jsxs("span", {
          children: [permissionsNotOnAppConfig.length > 0 && /*#__PURE__*/_jsx(StatusDot, {
            use: "warning",
            "data-selenium": "data-permissions-warning-dot"
          }), /*#__PURE__*/_jsx(FormattedMessage, {
            message: "app-governance-lib.modal.tabs.dataPermissions"
          })]
        }),
        children: /*#__PURE__*/_jsx(PanelSection, {
          children: /*#__PURE__*/_jsx(DataPermissions, {
            hasDataPermissions: hasDataPermissions,
            permissionsNotOnAppConfig: permissionsNotOnAppConfig,
            requiredAppPermissions: requiredAppPermissions,
            conditionallyRequiredAppPermissions: conditionallyRequiredAppPermissions,
            optionalAppPermissions: optionalAppPermissions,
            onOpenInstallPermissions: () => handleTabChange(MANAGEMENT_MODAL_PANEL_TABS.INSTALL_PERMISSIONS)
          })
        })
      }), /*#__PURE__*/_jsx(Tab, {
        tabId: MANAGEMENT_MODAL_PANEL_TABS.REVOKE_APPROVAL,
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "app-governance-lib.modal.tabs.revokeApproval"
        }),
        "data-selenium": "revoke-approval-tab",
        children: /*#__PURE__*/_jsx(PanelSection, {
          children: /*#__PURE__*/_jsx(RevokeApproval, {})
        })
      })]
    })
  });
}
function ErrorState() {
  return /*#__PURE__*/_jsx(Alert, {
    type: "danger",
    titleText: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "app-governance-lib.errorBoundary.default.title"
    }),
    margin: "600",
    children: /*#__PURE__*/_jsx(Paragraph, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.modal.error"
      })
    })
  });
}
function ManagementModalPanelHeader({
  onClose
}) {
  const {
    requestAction
  } = useUnsavedChanges();
  const {
    appName
  } = useApp();
  const handleClose = () => {
    requestAction(onClose, ACTION_TYPES.CLOSE);
  };
  return /*#__PURE__*/_jsxs(PanelHeader, {
    children: [/*#__PURE__*/_jsx(H2, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "app-governance-lib.modal.management",
        options: {
          appName
        }
      })
    }), /*#__PURE__*/_jsx(DialogCloseButton, {
      onClick: handleClose
    })]
  });
}
function ModalFooter({
  selectedTab
}) {
  switch (selectedTab) {
    case MANAGEMENT_MODAL_PANEL_TABS.DATA_PERMISSIONS:
      return /*#__PURE__*/_jsx(DataPermissionsFooter, {});
    case MANAGEMENT_MODAL_PANEL_TABS.INSTALL_PERMISSIONS:
      return /*#__PURE__*/_jsx(InstallPermissionsFooter, {});
    default:
      return null;
  }
}
function ModalContent({
  selectedTab,
  setSelectedTab,
  installCount
}) {
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(PanelBody, {
      children: /*#__PURE__*/_jsx(ManagementModalPanelContent, {
        installCount: installCount,
        selectedTab: selectedTab,
        setSelectedTab: setSelectedTab
      })
    }), /*#__PURE__*/_jsx(ModalFooter, {
      selectedTab: selectedTab
    })]
  });
}
function ManagementModalPanelContentWithData({
  installCount,
  selectedTab,
  setSelectedTab
}) {
  const {
    data: {
      optionalAppPermissions,
      requiredAppPermissions,
      conditionallyRequiredAppPermissions,
      installPermissionAccessState,
      entityTypeAndIds
    }
  } = useFetchModalPermissions();
  const isUngatedForTeamsGate = useTeamsGate();
  const gatedEntityTypeAndIds = filterGatedEntityTypes(entityTypeAndIds, isUngatedForTeamsGate);
  return /*#__PURE__*/_jsx(DataPermissionsProvider, {
    optionalAppPermissions: optionalAppPermissions,
    requiredAppPermissions: requiredAppPermissions,
    conditionallyRequiredAppPermissions: conditionallyRequiredAppPermissions,
    children: /*#__PURE__*/_jsx(InstallPermissionsProvider, {
      initialInstallPermissionAccessState: installPermissionAccessState,
      entityTypeAndIds: gatedEntityTypeAndIds,
      children: /*#__PURE__*/_jsx(InstallPermissionsDowngradeProvider, {
        children: /*#__PURE__*/_jsx(ModalContent, {
          installCount: installCount,
          selectedTab: selectedTab,
          setSelectedTab: setSelectedTab
        })
      })
    })
  });
}
function ManagementModalPanelInner({
  onClose,
  installCount,
  selectedTab,
  setSelectedTab
}) {
  const {
    requestAction
  } = useUnsavedChanges();
  const handleBackdropClick = useCallback(() => {
    requestAction(onClose, ACTION_TYPES.CLOSE);
  }, [requestAction, onClose]);
  return /*#__PURE__*/_jsxs(ModalPanel, {
    "data-selenium": "management-modal-panel",
    width: DIALOG_WIDTH,
    onBackdropClick: handleBackdropClick,
    children: [/*#__PURE__*/_jsx(ManagementModalPanelHeader, {
      onClose: onClose
    }), /*#__PURE__*/_jsx(SuspenseLoadingState, {
      errorComponent: ErrorState,
      children: /*#__PURE__*/_jsx(ManagementModalPanelContentWithData, {
        installCount: installCount,
        selectedTab: selectedTab,
        setSelectedTab: setSelectedTab
      })
    })]
  });
}
function ManagementModalPanelWithProviders({
  onClose,
  installCount,
  initialTab
}) {
  const [selectedTab, setSelectedTab] = useState(initialTab || MANAGEMENT_MODAL_PANEL_TABS.INSTALLED_BY);
  const {
    track
  } = useTracking();
  const {
    appId,
    appName
  } = useApp();
  useEffect(() => {
    track(EVENTS.PAGE_VIEWED, {
      screen: SCREENS.MANAGEMENT_MODAL_PANEL,
      subscreen: TAB_TO_SUBSCREEN[selectedTab],
      appId,
      appName
    });
  }, [track, selectedTab, appId, appName]);
  return /*#__PURE__*/_jsx(UnsavedChangesProvider, {
    children: /*#__PURE__*/_jsx(ManagementModalPanelInner, {
      onClose: onClose,
      installCount: installCount,
      selectedTab: selectedTab,
      setSelectedTab: setSelectedTab
    })
  });
}
function ManagementModalPanelWithTracking({
  onClose,
  appId,
  onUserUninstall,
  installCount: initialInstallCount,
  onRevokeApproval,
  onInstallPermissionsSave,
  initialTab = MANAGEMENT_MODAL_PANEL_TABS.INSTALLED_BY
}) {
  const handleRevokeApproval = () => {
    onRevokeApproval === null || onRevokeApproval === void 0 || onRevokeApproval(appId);
    onClose();
  };
  const [installCount, setInstallCount] = useState(initialInstallCount);
  return /*#__PURE__*/_jsx(UninstallCallbackProvider, {
    onUserUninstall: user => {
      onUserUninstall === null || onUserUninstall === void 0 || onUserUninstall(user);
      if (installCount !== undefined) {
        // We can guarantee that installCount is not undefined
        // because it is set in the parent component, so don't need to check the previous value.
        setInstallCount(installCount - 1);
      }
    },
    children: /*#__PURE__*/_jsx(RevokeApprovalCallbackProvider, {
      onRevokeApproval: handleRevokeApproval,
      children: /*#__PURE__*/_jsxs(InstallPermissionsSaveCallbackProvider, {
        onInstallPermissionsSave: (installPermissionAccessState, newInstallCount) => {
          onInstallPermissionsSave === null || onInstallPermissionsSave === void 0 || onInstallPermissionsSave(installPermissionAccessState, newInstallCount);
          if (newInstallCount !== undefined) {
            setInstallCount(newInstallCount);
          }
        },
        children: [/*#__PURE__*/_jsx(FloatingAlertList, {}), /*#__PURE__*/_jsx(ManagementModalPanelWithProviders, {
          onClose: onClose,
          installCount: installCount,
          initialTab: initialTab
        })]
      })
    })
  });
}
export function ManagementModalPanel({
  onClose,
  appName,
  appId,
  currentUserId,
  onUserUninstall,
  installCount,
  onRevokeApproval,
  onInstallPermissionsSave,
  initialTab = MANAGEMENT_MODAL_PANEL_TABS.INSTALLED_BY,
  tracker,
  isUngatedForTeamsSelection
}) {
  return /*#__PURE__*/_jsx(AppProvider, {
    appName: appName,
    appId: appId,
    currentUserId: currentUserId,
    children: /*#__PURE__*/_jsx(TrackingProvider, {
      tracker: tracker,
      children: /*#__PURE__*/_jsx(TeamsGateProvider, {
        isUngatedForTeamsGate: isUngatedForTeamsSelection,
        children: /*#__PURE__*/_jsx(ManagementModalPanelWithTracking, {
          onClose: onClose,
          appId: appId,
          onUserUninstall: onUserUninstall,
          installCount: installCount,
          onRevokeApproval: onRevokeApproval,
          onInstallPermissionsSave: onInstallPermissionsSave,
          initialTab: initialTab
        })
      })
    })
  });
}
export default ManagementModalPanel;